import React from "react"
import { Box } from "@chakra-ui/react"

import BrokerGuidancePanel, { useBrokerGuidanceColors } from "./BrokerGuidancePanel"
import {
  ChecklistRow,
  GuidanceAccordionBody,
  GuidanceBlock,
  GuidanceIntro,
} from "./ibkrGuidanceShared"

export interface OandaTransactionsImportGuidanceProps {
  compact?: boolean
  /** When false, omit Tessera account notes (demo has no account chrome). */
  showAccountNotes?: boolean
}

/**
 * OANDA HUB → Reports → Transaction History → Export to csv.
 * @see https://help.oanda.com/us/en/faqs/transaction-history.htm
 */
const OandaTransactionsImportGuidance = ({
  compact = false,
  showAccountNotes = true,
}: OandaTransactionsImportGuidanceProps): React.ReactElement => {
  const colors = useBrokerGuidanceColors()

  return (
    <BrokerGuidancePanel
      title="How to export OANDA Transaction History"
      compact={compact}
      collapsible
    >
      <GuidanceAccordionBody>
        <GuidanceIntro color={colors.bodyColor} className="!mb-0">
          Upload the HUB{" "}
          <Box as="span" fontWeight="semibold">
            Transaction History
          </Box>{" "}
          CSV, not an unrelated OANDA export (for example a crypto-only trade page).
        </GuidanceIntro>

        <GuidanceBlock title="Export steps">
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Log in to the OANDA{" "}
            <Box as="span" fontWeight="semibold">
              HUB
            </Box>
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Open{" "}
            <Box as="span" fontWeight="semibold">
              Reports
            </Box>{" "}
            →{" "}
            <Box as="span" fontWeight="semibold">
              Transaction History
            </Box>
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Select the{" "}
            <Box as="span" fontWeight="semibold">
              account
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              start date
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              end date
            </Box>
            , and{" "}
            <Box as="span" fontWeight="semibold">
              timezone
            </Box>
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Click{" "}
            <Box as="span" fontWeight="semibold">
              Show Transactions
            </Box>
            , then{" "}
            <Box as="span" fontWeight="semibold">
              Export to csv
            </Box>
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Upload the downloaded CSV here
          </ChecklistRow>
        </GuidanceBlock>

        <GuidanceBlock title="Required columns">
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            <Box as="span" fontWeight="semibold">
              TRANSACTION DATE
            </Box>{" "}
            (with timezone abbrev or offset),{" "}
            <Box as="span" fontWeight="semibold">
              TRANSACTION TYPE
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              INSTRUMENT
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              PRICE
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              UNITS
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              DIRECTION
            </Box>
            ,{" "}
            <Box as="span" fontWeight="semibold">
              PL
            </Box>
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Also present on the HUB export: TICKET, DETAILS, SPREAD COST, FINANCING, COMMISSION,
            BALANCE, and optional stop / take-profit fields
          </ChecklistRow>
        </GuidanceBlock>

        <GuidanceBlock title="Timezone">
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            Pick the timezone carefully in the HUB before exporting. OANDA embeds the zone (or
            offset) in the{" "}
            <Box as="span" fontWeight="semibold">
              TRANSACTION DATE
            </Box>{" "}
            column. Tessera reads that from the file. You do not set a separate statement timezone
            on import.
          </ChecklistRow>
          <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
            The HUB default is GMT −12. If you leave it unchanged, timestamps use that offset.
          </ChecklistRow>
        </GuidanceBlock>

        {showAccountNotes ? (
          <GuidanceBlock title="Account in Tessera">
            <ChecklistRow className="!mb-0" iconColor={colors.iconColor} labelColor={colors.labelColor}>
              Enter the{" "}
              <Box as="span" fontWeight="semibold">
                account code
              </Box>{" "}
              you want Tessera to use for this history (usually your OANDA account ID). Re-importing
              updates that same account.
            </ChecklistRow>
          </GuidanceBlock>
        ) : null}
      </GuidanceAccordionBody>
    </BrokerGuidancePanel>
  )
}

export default OandaTransactionsImportGuidance
